import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { useFormik, Form, FormikProvider } from 'formik';
import * as Yup from 'yup';
import { Autocomplete, Box, Stack, TextField } from '@mui/material';

import GlobalDialog from '../../components/mui/Dialogue/GlobalDialog';
import { AddButton, CancelButton, SaveButton } from '../../components/mui/Buttons/Buttons';
import {
  createSkillxAssignment,
  getSkillxAssignments,
} from '../../redux/features/skillxSlice';
import { getAvailableCourses } from '../../redux/features/coursesSlice';

const ValidationSchema = Yup.object().shape({
  title: Yup.string().trim().required('Title is required').max(200, 'Title is too long'),
  description: Yup.string().trim().required('Description is required'),
  course_available_id: Yup.number()
    .typeError('Course ID must be a number')
    .integer('Course ID must be a whole number')
    .positive('Course ID must be positive')
    .nullable(),
  max_grade: Yup.number()
    .typeError('Max grade must be a number')
    .min(1, 'Max grade must be at least 1')
    .nullable(),
});

export default function AddSkillxAssignment({ dispatchparams }) {
  const [open, setOpen] = useState(false);
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { loading } = useSelector((state) => state.skillx);
  const { availableCourse } = useSelector((state) => state.courses);
  const courseOptions = Array.isArray(availableCourse) ? availableCourse : [];

  // Fetch the SkillX course list once the dialog is opened.
  useEffect(() => {
    if (open && !courseOptions.length) {
      dispatch(getAvailableCourses({ skillx: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      title: '',
      module_label: '',
      course_available_id: '',
      description: '',
      instructions: '',
      due_date: '',
      max_grade: 100,
    },
    validationSchema: ValidationSchema,
    onSubmit: (values) => {
      const payload = {
        title: values.title,
        description: values.description,
        module_label: values.module_label || null,
        instructions: values.instructions || null,
        course_available_id: values.course_available_id || null,
        due_date: values.due_date || null,
        max_grade: values.max_grade || 100,
      };

      dispatch(createSkillxAssignment(payload)).then((res) => {
        if (res.payload?.status === 'success') {
          enqueueSnackbar(res.payload.message, { variant: 'success' });
          dispatch(getSkillxAssignments(dispatchparams));
          formik.resetForm();
          setOpen(false);
        } else {
          enqueueSnackbar(res.payload?.message || 'Failed to create assignment', {
            variant: 'error',
          });
        }
      });
    },
  });

  const { errors, touched, values, handleChange, handleSubmit } = formik;

  return (
    <GlobalDialog
      title="Add Assignment"
      open={open}
      setOpen={setOpen}
      fullWidth
      maxWidth="md"
      closeButton
      popupButton={
        <AddButton size="medium" action={() => setOpen(true)}>
          Add Assignment
        </AddButton>
      }
      content={
        <FormikProvider value={formik}>
          <Form onSubmit={handleSubmit}>
            <Stack direction="column" spacing={2} sx={{ pt: 1 }}>
              <TextField
                name="title"
                label="Title"
                fullWidth
                value={values.title}
                onChange={handleChange}
                error={touched.title && Boolean(errors.title)}
                helperText={touched.title && errors.title}
              />

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  name="module_label"
                  label="Module (optional)"
                  fullWidth
                  value={values.module_label}
                  onChange={handleChange}
                />
                <Autocomplete
                  fullWidth
                  options={courseOptions}
                  getOptionLabel={(option) => option?.courseName || ''}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={
                    courseOptions.find((c) => c.id === values.course_available_id) || null
                  }
                  onChange={(e, newValue) =>
                    formik.setFieldValue('course_available_id', newValue ? newValue.id : '')
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="course_available_id"
                      label="Course (optional)"
                      error={touched.course_available_id && Boolean(errors.course_available_id)}
                      helperText={
                        (touched.course_available_id && errors.course_available_id) ||
                        'The course this assignment belongs to'
                      }
                    />
                  )}
                />
              </Stack>

              <TextField
                name="description"
                label="Description"
                fullWidth
                multiline
                minRows={3}
                value={values.description}
                onChange={handleChange}
                error={touched.description && Boolean(errors.description)}
                helperText={touched.description && errors.description}
              />

              <TextField
                name="instructions"
                label="Instructions (optional)"
                fullWidth
                multiline
                minRows={2}
                value={values.instructions}
                onChange={handleChange}
              />

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  name="due_date"
                  label="Due Date (optional)"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={values.due_date}
                  onChange={handleChange}
                />
                <TextField
                  name="max_grade"
                  label="Max Grade"
                  type="number"
                  fullWidth
                  value={values.max_grade}
                  onChange={handleChange}
                  error={touched.max_grade && Boolean(errors.max_grade)}
                  helperText={touched.max_grade && errors.max_grade}
                />
              </Stack>
            </Stack>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 3 }}>
              <Stack direction="row" spacing={1}>
                <CancelButton size="small" action={() => setOpen(false)}>
                  Cancel
                </CancelButton>
                <SaveButton disabled={loading} size="small" type="submit">
                  {loading ? 'Please wait...' : 'Create'}
                </SaveButton>
              </Stack>
            </Box>
          </Form>
        </FormikProvider>
      }
    />
  );
}
